/**
 * @file index.js
 * @version 1.5.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2026-10-28
 *
 * 1.5.1 (2026-10-28): SafeArea.css — the iPhone's status bar, notch and home
 * indicator kept clear app-wide.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './SafeArea.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { installReadOnlyFetchGuard } from './utils/readOnlyFetch';
import { installOverlayStacking } from './utils/overlayStacking';
import { installGuidedShiftWatch } from './utils/guidedMode';

// Installed before anything renders so the very first request of the session is
// already covered. See utils/readOnlyFetch.js for why this exists at all.
installReadOnlyFetchGuard();

// Modal overlays stack in the order they are opened rather than in the order
// React appended their portals, and the last one touched comes to the front.
// Installed here for the same reason as the guard above and the backdrop guard
// below: it is a rule about every overlay in the app, and only a handful of
// them go through components/Modal.js. See utils/overlayStacking.js.
installOverlayStacking();

// Holding SHIFT shows every slide-out label and nav tooltip at once, with no
// wait. Installed here rather than in a component because it is a document-
// level key watch feeding a <body> attribute that CSS reads — the same shape
// as the guards around it. See utils/guidedMode.js.
installGuidedShiftWatch();

// ============================================================================
// Global guard — stop number inputs from being accidentally changed by the
// mouse scroll wheel or the Up/Down arrow keys. These native behaviours cause
// data-entry mistakes (e.g. scrolling a price-list scrolls the focused qty up
// by 1). Applied once at the document level so every <input type="number"> is
// covered without having to touch each call-site.
// ============================================================================
const isNumberInput = (el) =>
  el instanceof HTMLInputElement && el.type === 'number';

document.addEventListener('wheel', (e) => {
  if (isNumberInput(document.activeElement) && document.activeElement === e.target) {
    // Blurring removes focus so the native step-on-wheel behaviour can't fire,
    // and the page's scroll isn't hijacked either.
    document.activeElement.blur();
  }
}, { passive: true });

document.addEventListener('keydown', (e) => {
  if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && isNumberInput(e.target)) {
    e.preventDefault();
  }
}, true); // capture phase so handlers on individual inputs can't re-enable it

// ============================================================================
// Global guard — disable click-outside-to-close on modal backdrops system-wide.
// Modals must be dismissed via explicit Close / Cancel / Save buttons (the X
// in the header, the footer buttons, etc.). A backdrop click was too easy to
// trigger by accident mid-edit and dropped in-flight form state. Capture
// phase + stopPropagation here means individual modals' onClick={handleClose}
// on the overlay div never fires — no per-component edits needed. Clicks on
// the modal content bubble up with their own (inner) target, so this check
// leaves them alone.
// ============================================================================
const OVERLAY_CLASSES = ['modal-overlay', 'link-pos-dialog-overlay'];
document.addEventListener('click', (e) => {
  const target = e.target;
  if (!(target instanceof Element) || !target.classList) return;
  if (OVERLAY_CLASSES.some(cls => target.classList.contains(cls))) {
    e.stopPropagation();
    e.preventDefault();
  }
}, true);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
